import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Store } from 'repatch';

import { GraphQLOperationFailed } from '../adapter/GraphQLClient';
import State from '../core/State';
import { demoProjectData } from '../data/demo_project/project';
import { ProjectDetailsForShowFragment } from '../generated/graphql';
import { getBriefFileBucketIds, getBriefFiles, getProjectForBrief, ProjectForBrief } from '../graphql/project';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import CreationWebApi from '../service/persistent/CreationWebApi';
import DownloadApi from '../service/persistent/DownloadApi';
import ProjectChangeWebApi from '../service/persistent/ProjectChangeWebApi';
import ProjectWebApi from '../service/persistent/ProjectWebApi';
import { setUploadedAdPlatformKeysMap } from '../thunk/creativeSending';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import {
  deleteProjectIssueComment,
  disposeProjectAndCreations,
  finishFetchingAssetFiles,
  finishFetchingBriefFiles,
  finishFetchingProject,
  setProjectIssue,
  setProjectIssueComment,
  startFetchingAssetFiles,
  startFetchingBriefFiles,
  startFetchingProject,
  updateProjectIssueComment,
} from '../thunk/project';
import { setIsRemovedLocalData } from '../thunk/projectEditing';
import {
  finishFetchingProjectChanges,
  setBeforeBriefFiles,
  setLatestBriefFiles,
  startFetchingProjectChanges,
} from './../thunk/projectChange';

class ShowProjectUsecase {
  private store: Store<State>;
  private belfastClient: BelfastClient;
  private projectWebApi: ProjectWebApi;
  private projectChangeWebApi: ProjectChangeWebApi;
  private downloadApi: DownloadApi;
  private messageDispatcher: MessageDispatcher;
  private errorPresenter: ErrorPresenter;
  private hugotApiBaseUrl: string;

  constructor({
    store,
    belfastClient,
    projectWebApi,
    projectChangeWebApi,
    downloadApi,
    messageDispatcher,
    errorPresenter,
    hugotApiBaseUrl,
  }: {
    store: Store<State>;
    belfastClient: BelfastClient;
    creationWebApi: CreationWebApi;
    projectWebApi: ProjectWebApi;
    projectChangeWebApi: ProjectChangeWebApi;
    downloadApi: DownloadApi;
    messageDispatcher: MessageDispatcher;
    errorPresenter: ErrorPresenter;
    hugotApiBaseUrl: string;
  }) {
    this.store = store;
    this.belfastClient = belfastClient;
    this.projectWebApi = projectWebApi;
    this.downloadApi = downloadApi;
    this.messageDispatcher = messageDispatcher;
    this.errorPresenter = errorPresenter;
    this.projectChangeWebApi = projectChangeWebApi;
    this.hugotApiBaseUrl = hugotApiBaseUrl;
  }

  public prepareProject = async (projectId: string): Promise<void> => {
    const demoProject = demoProjectData.find((v) => v.data.project.id === projectId);
    if (demoProject) {
      this.prepareDemoProject(projectId);
      return;
    }

    this.store.dispatch(startFetchingProject());
    this.store.dispatch(startFetchingAssetFiles());
    this.store.dispatch(startFetchingBriefFiles());

    const language = this.store.getState().language;

    let project: ProjectForBrief & ProjectDetailsForShowFragment;

    try {
      project = await this.projectWebApi.getByIdForShowPage(projectId, language);
    } catch (e) {
      if (e instanceof GraphQLOperationFailed) {
        if (e.type === 'UnauthorizedError') {
          console.error(e.type);
          this.errorPresenter.showAsFatal(new Error('duplicate_business_account_error'));
        }
      }
      throw e;
    }

    const briefFileBucketIds = getBriefFileBucketIds(
      project.creativeBriefDefinition,
      project.creativeBriefValue.values,
    );

    const [briefFiles, assetFiles] = await Promise.all([
      getBriefFiles(briefFileBucketIds, this.belfastClient),
      project.bucketId != null ? this.belfastClient.getAllFilesInBucket(project.bucketId) : ([] as FileReference[]),
    ]);

    const uploadedAdPlatformKeysMap = new Map<string, Array<string>>();
    project.creationSet?.creations.forEach((creation) => {
      creation.deliverables.forEach((deliverable) => {
        uploadedAdPlatformKeysMap.set(deliverable.id, deliverable.uploadedAdPlatformKeys || []);
      });
    });

    // local storage保存の指示書の下書きの破棄
    const localProject = JSON.parse(window.localStorage.getItem(`project_brief_${project.id}`) || '{}');
    if (Object.keys(localProject).length && project.versionId !== localProject.versionId) {
      this.store.dispatch(setIsRemovedLocalData(true));
      window.localStorage.removeItem(`project_brief_${project.id}`);
    }

    this.store.dispatch(setUploadedAdPlatformKeysMap(uploadedAdPlatformKeysMap));
    this.store.dispatch(setLastAccessedOrganization(project.organization));
    this.store.dispatch(finishFetchingAssetFiles(assetFiles));
    this.store.dispatch(finishFetchingBriefFiles(briefFiles));
    this.store.dispatch(finishFetchingProject(project));
  };

  public prepareProjectChanges = async (project: ProjectForBrief & ProjectDetailsForShowFragment): Promise<void> => {
    const demoProject = demoProjectData.find((v) => v.data.project.id === project.id);
    if (demoProject) {
      return;
    }

    this.store.dispatch(startFetchingProjectChanges());

    const projectChange = await this.projectChangeWebApi.get(project);
    if (projectChange) {
      /**
       * Brief File取得
       */
      const latestVersion = projectChange.latestVersion;
      const latestBriefFileBucketIds = getBriefFileBucketIds(
        project.creativeBriefDefinition,
        latestVersion.creativeBriefValues,
      );

      const beforeVersion = projectChange.beforeVersion;
      const beforeBriefFileBucketIds = getBriefFileBucketIds(
        project.creativeBriefDefinition,
        beforeVersion.creativeBriefValues,
      );

      const [latestBriefFiles, beforeBriefFiles] = await Promise.all([
        getBriefFiles(latestBriefFileBucketIds, this.belfastClient),
        getBriefFiles(beforeBriefFileBucketIds, this.belfastClient),
      ]);
      this.store.dispatch(setLatestBriefFiles(latestBriefFiles));
      this.store.dispatch(setBeforeBriefFiles(beforeBriefFiles));
    }
    this.store.dispatch(finishFetchingProjectChanges(projectChange));
  };

  public prepareDemoProject = async (projectId: string): Promise<void> => {
    this.store.dispatch(startFetchingProject());

    const demoProject = demoProjectData.find((v) => v.data.project.id === projectId);
    const project = demoProject!.data.project as ProjectDetailsForShowFragment;

    this.store.dispatch(finishFetchingProject(getProjectForBrief(project)));
  };

  public downloadAssets = async ({
    buckets = [],
    files = [],
    projects = [],
  }: {
    buckets: ReadonlyArray<string>;
    files: ReadonlyArray<string>;
    projects: ReadonlyArray<string>;
  }): Promise<void> => {
    const project = this.store.getState().project.project;

    if (project == null) {
      throw new Error('<state>.project has not been prepared.');
    }
    if (project.bucketId == null) {
      throw new Error('<state>.project.bucketId must be string.');
    }

    try {
      await this.downloadApi.download({
        buckets,
        files,
        projects,
        bucketIdForFiles: project.bucketId,
        handler: (response: any) => {
          // 定期的に圧縮状態を確認するタイマーを設定します
          const timerId = setInterval(() => {
            const downloadKey = response.data.download_key;
            this.checkCompressionIsFinished({
              downloadKey: downloadKey,
              finishedHandler: () => {
                this.messageDispatcher.notifyUnimportant('project_basic_setting_detail.assets.download_all.compressed');
                this.downloadCompressedFile({ downloadKey: downloadKey });
                clearInterval(timerId);
              },
            });
          }, 1000);
        },
      });
      this.messageDispatcher.notifyUnimportant('project_basic_setting_detail.assets.download_all.succeeded');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  private getDownload = async ({
    downloadKey,
    handler,
  }: {
    downloadKey: string;
    handler?: (response: any) => void;
  }) => {
    try {
      await this.downloadApi.get({ downloadKey, handler });
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  // 指定されたダウンロードキーのアイテムの圧縮処理が完了しているか否かを検証し、その結果を返却します
  private checkCompressionIsFinished = async ({
    downloadKey,
    finishedHandler,
  }: {
    downloadKey: string;
    finishedHandler: () => void;
  }) => {
    this.getDownload({
      downloadKey: downloadKey,
      handler: (response: any) => {
        if (response.data.status === 'finished_compression') {
          finishedHandler();
        }
      },
    });
  };

  private downloadCompressedFile = async ({ downloadKey }: { downloadKey: string }) => {
    try {
      this.messageDispatcher.notifyUnimportant('project_basic_setting_detail.assets.download_all.compressed');
      await this.downloadApi.downloadFile({ downloadKey: downloadKey, baseUrl: this.hugotApiBaseUrl });
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
  };

  public removeLocalDraftProject = (): void => {
    this.store.dispatch(setIsRemovedLocalData(false));
  };

  public disposeProject = (): void => {
    this.store.dispatch(disposeProjectAndCreations());
  };

  public createProjectIssue = async (projectId: string, body: string): Promise<void> => {
    try {
      const projectIssue = await this.projectWebApi.createProjectIssue(projectId, 'Untitled', body);

      this.store.dispatch(setProjectIssue(projectIssue));

      this.messageDispatcher.notifyUnimportant('show_project_page.project_issue.message.post');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('show_project_page.project_issue.message.post.failed'));
    }
  };

  public createProjectIssueComment = async (id: string, comment: string): Promise<void> => {
    try {
      const projectIssueComment = await this.projectWebApi.createProjectIssueComment(id, comment);

      this.store.dispatch(setProjectIssueComment(projectIssueComment));

      this.messageDispatcher.notifyUnimportant('show_project_page.project_issue.message.post');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('show_project_page.project_issue.message.post.failed'));
      throw err;
    }
  };

  public updateProjectIssueComment = async (id: string, comment: string): Promise<void> => {
    try {
      const projectIssueComment = await this.projectWebApi.updateProjectIssueComment(id, comment);

      this.store.dispatch(updateProjectIssueComment(projectIssueComment));

      this.messageDispatcher.notifyUnimportant('show_project_page.project_issue.message.update');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('show_project_page.project_issue.message.update.failed'));
      throw err;
    }
  };

  public deleteProjectIssueComment = async (id: string): Promise<void> => {
    try {
      await this.projectWebApi.deleteProjectIssueComment(id);

      this.store.dispatch(deleteProjectIssueComment(id));

      this.messageDispatcher.notifyUnimportant('show_project_page.project_issue.message.delete');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(new Error('show_project_page.project_issue.message.delete.failed'));
      throw err;
    }
  };
}

export default ShowProjectUsecase;
